import { RefObject } from 'preact';
import { useLayoutEffect, useEffect, useMemo, useState, useRef, useCallback } from 'preact/hooks';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { autoUpdate, arrow } from '@floating-ui/dom';
import {
  useFloating,
  shift,
  flip,
  size,
  offset,
  limitShift,
  VirtualElement,
  logicalSide
} from '../hooks/PRIVATE_useFloating';
import { Offset, Placement, Coords, PositionData } from './Floating.types';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import { useOutsideClick } from '../hooks/UNSAFE_useOutsideClick';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { useUser } from '../hooks/UNSAFE_useUser';
import { FloatingTail } from './FloatingTail';
import { classNames } from '../utils/UNSAFE_classNames';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { FloatingStyles, floatingVisibilityStyles } from './themes/FloatingStyles.css';
import { FloatingRedwoodTheme } from './themes/redwood/FloatingTheme';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { floatingVars } from './themes/FloatingContract.css';
import type { ColorProps } from '../utils/UNSAFE_interpolations/colors';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children' | 'class'>;
/**
 * Specifies the flipping behavior along the respective axis.
 */
export type FlipOptions = {
  /**
   * If set to true, enables collision detection on the main axis,
   * which runs along the floating placement side.
   */
  mainAxis?: boolean;
  /**
   * If set to true, enables collision detection on the cross axis,
   * which runs along the floating placement side.
   */
  crossAxis?: boolean;
  /**
   * Specifies an array of placements to try
   * if the preferred initial placement doesn’t fit on the axes
   * in which collision is detected.
   */
  fallbackPlacements?: Array<Placement>;
};

/**
 * Specifies the shift behavior along the respective axis.
 * When true, the floating element will shift to prevent overflow.
 */
export type ShiftOptions = {
  /**
   * main axis in which shifting is applied. Default <code>true</code>.
   */
  mainAxis?: boolean;
  /**
   * cross axis in which shifting is applied. Default <code>false</code>.
   */
  crossAxis?: boolean;
};

/**
 * This describes the virtual padding around the collision boundary (currently the view port).
 * This could be extended in the future,
 * i.e. property to define what is a boundary ( a view port, a document, some other html element)
 */
export type BoundaryOptions = {
  padding?:
    | number
    | {
        top: number;
        right: number;
        bottom: number;
        left: number;
      };
};
/**
 * Specifies the resizing behaviour for max-height and max-width of the floating component.
 * The floating element will change the size depending on the available space.
 * isMaxHeightAdjusted/isMaxWidthAdjusted - sets the max-height/max-width of the floating to the available space till the collision.
 * maxHeightCeiling/maxWidthCeiling - this is the limit for max-height/max-width that should not be exceeded, even if there is more available space till the collision.
 */
export type SizeOptions = {
  isMaxWidthAdjusted?: boolean;
  isMaxHeightAdjusted?: boolean;
  maxHeightCeiling?: number;
  maxWidthCeiling?: number;
};

type Props = IntrinsicProps &
  Pick<ColorProps, 'backgroundColor'> & {
    /**
     * <p>Placement of the floating element relative to its reference element.</p>
     * <p>Placement consists of two parts, the first part is the side, the second is the alignment.</p>
     * <p>Possible values are:</p>
     * <p> <code>'top' </code> <code>'top-start' </code> <code> 'top-end' </code> <code> 'top-start-corner' </code> <code> 'top-end-corner' </code>
     * <code> 'end' </code> <code> 'end-top'  </code> <code> 'end-bottom' </code> <code> 'end-top-corner' </code> <code> 'end-bottom-corner' </code>
     * <code> 'bottom'  </code> <code> 'bottom-start'  </code> <code> 'bottom-end' </code> <code> 'bottom-start-corner' </code> <code> 'bottom-end-corner' </code>
     * <code> 'start' </code> <code> 'start-top'  </code> <code> 'start-bottom' </code> <code> 'start-top-corner' </code> <code> 'start-bottom-corner' </code>
     * </p>
     */
    placement?: Placement;

    /**
     * Trigger element reference.
     */
    anchorRef: RefObject<Element | Coords>;

    /**
     * Placement offset.
     * Offset displaces the floating element from its core placement along the specified axes.
     * Offset could be a number or an object
     * A number represents the distance (gutter or margin) between the floating element and the reference element.
     * This is shorthand for mainAxis.
     * <pre>
     *   {
     *     mainAxis?: number;
     *     crossAxis?: number;
     *   }
     * </pre>
     * <p><code>mainAxis</code> - represents the distance (gutter or margin) between the floating element and the reference element.</p>
     * <p><code>crossAxis</code> - represents the shifting between the floating element and the reference element.<p>
     */
    offsetValue?: Offset;

    /**
     * Click outside callback.
     */
    onClickOutside?: (event: MouseEvent) => void;

    /**
     * FlipOptions object
     * <pre>
     * type FlipOptions = {
     *     mainAxis?: boolean;
     *     crossAxis?: boolean;
     *     fallbackPlacements?: Array<Placement>;
     *   }
     * </pre>
     * <p>Specifies the flipping behavior along the respective axis.<p>
     * <p><code>mainAxis</code> - If set to true, enables collision detection on the main axis, which runs along the floating placement side.
     * Default is <code>true</code>.
     * </p>
     * <p><code>crossAxis</code> - If set to true, enables collision detection on the cross axis, which runs along the floating placement alignment.
     * Default is <code>false</code>.
     * </p>
     * <p><code>fallbackPlacements</code> - specifies an array of placements to try if the preferred initial placement doesn’t fit on the axes in which collision is detected.
     * The fallback placements will be tried in the order they are specified in the array.
     * The following placements are allowed as values of the array:
     * <code>'top'</code>, <code>'top-start'</code>, <code>'top-end'</code>, <code>'start'</code>, <code>'start-top'</code>, <code>'start-bottom'</code>, <code>'bottom'</code>,
     * <code>'bottom-start'</code>, <code>'bottom-end'</code>, <code>'end'</code>, <code>'end-top'</code>, <code>'end-bottom'</code>.
     * <p>
     * If not specified, the default behaviour is to flip to the side <code>opposite</code> to the one in which collision is detected
     * </p>
     * </p>
     */
    flipOptions?: FlipOptions;

    /**
     * Specifies the shift behavior along the respective axis. When true, the floating element will shift to prevent overflow.
     * <pre>
     * type ShiftOptions = {
     *  mainAxis?: boolean;
     *  crossAxis?: boolean;
     * };
     * </pre>
     * <p><code>mainAxis</code> - main axis in which shifting is applied. Default <code>true</code>.</p>
     * <p><code>crossAxis</code> - cross axis in which shifting is applied. Default <code>false</code>.</p>
     */
    shiftOptions?: ShiftOptions;

    /**
     *  Triggered when placement or coordinates are changed after collision is detected
     */
    onPosition?: (data: PositionData) => void;

    /**
     * Tail
     */
    tail?: 'none' | 'simple';

    /**
     * Specifies the resizing behaviour for max-height and max-width of the floating component.
     * <pre>
     * type SizeOptions = {
     *    isMaxWidthAdjusted?: boolean;
     *    isMaxHeightAdjusted?: boolean;
     *    maxHeightCeiling?: number;
     *    maxWidthCeiling?: number;
     *  }
     * </pre>
     * <p><code>isMaxHeightAdjusted</code> and <code>isMaxWidthAdjusted</code> - sets the max-height/max-width of the floating to the available space till the collision.
     * When either of the options isMaxHeightAdjusted, isMaxWidthAdjusted is true, the floating element will change the size depending on the available space.
     * Default values are <code>'false'</code>.</p>
     * <p><code>maxHeightCeiling</code> and <code>maxWidthCeiling</code> - are maximal values that could not be exceeded.
     * Defaults are undefined and it means to use full available width/height.</p>
     */
    sizeOptions?: SizeOptions;

    /**
     * <p>This describes the virtual padding around the collision boundary (currently the view port).</p>
     * <p>This could be extended in the future,
     * i.e. property to define what is a boundary
     * ( a view port, a document, some other html element)</p>
     * <pre>
     * type BoundaryOptions = {
     *  padding?: number | {
     *    top: number;
     *    right: number;
     *    bottom: number;
     *    left: number;
     *  };
     * }
     * </pre>
     * Default value for padding is 0.
     */
    boundaryOptions?: BoundaryOptions;
  };

function isElement(value: any): value is Element {
  return value instanceof Element;
}

/**
 * A floating component allows the user to create a floating element that is displayed relative to an anchor element or pointer coordinates
 * and at the same time on the "top layer" of all other elements.
 * The Floating component is a base component and is often used by such components as Popup, Tooltip, SelectMultiple, Menu and other components.
 */
export const Floating = forwardRef(
  (
    {
      children,
      backgroundColor,
      onClickOutside,
      placement = 'bottom',
      anchorRef,
      offsetValue,
      class: className,
      tail = 'none',
      flipOptions = { mainAxis: true, crossAxis: false },
      shiftOptions = { mainAxis: true, crossAxis: false },
      sizeOptions = { isMaxHeightAdjusted: false, isMaxWidthAdjusted: false },
      boundaryOptions = { padding: 0 },
      onPosition
    }: Props,
    ref: ForwardedRef<HTMLElement | null>
  ) => {
    const arrowRef = useRef<HTMLDivElement>(null);
    const [positionData, setPositionData] = useState<PositionData>();

    const [previousAnchor, setPreviousAnchor] = useState<Element | Coords | null>(null);

    const { styles, baseTheme } = useComponentTheme<never, FloatingStyles>(FloatingRedwoodTheme);

    // we need to be sure anchorRef.current exists in first call
    // to be able to set the previousAnchor state
    useLayoutEffect(() => {
      setPreviousAnchor(anchorRef.current);
    }, [anchorRef]);

    // In case anchorRef.current changes and we need to update floating position
    // when it changes, we need to have copy in the state.
    // For example when anchorRef is Coords( virtual element)
    if (anchorRef.current !== previousAnchor) {
      setPreviousAnchor(anchorRef.current);
    }

    const { direction } = useUser();
    const isRtl = direction === 'rtl';

    const _onPosition = useCallback(
      (data: PositionData) => {
        setPositionData(data);
        onPosition?.(data);
      },
      [onPosition]
    );

    const middleware = [
      offset(offsetValue),
      flip({
        mainAxis: flipOptions.mainAxis,
        crossAxis: flipOptions.crossAxis,
        fallbackPlacements: flipOptions.fallbackPlacements?.map((placement) =>
          logicalSide(placement, direction)
        ),
        padding: boundaryOptions.padding
      }),
      shift({
        mainAxis: shiftOptions.mainAxis,
        crossAxis: shiftOptions.crossAxis,
        limiter: limitShift({
          mainAxis: shiftOptions.mainAxis,
          crossAxis: shiftOptions.crossAxis
        })
      }),
      ...(arrowRef.current
        ? [
            arrow({
              element: arrowRef.current
            })
          ]
        : [])
    ];
    if (sizeOptions.isMaxHeightAdjusted || sizeOptions.isMaxWidthAdjusted) {
      middleware.push(
        size({
          apply({ availableWidth, availableHeight, elements }) {
            if (sizeOptions.isMaxHeightAdjusted) {
              if (parseInt(elements.floating.style.maxHeight) !== availableHeight) {
                Object.assign(elements.floating.style, {
                  maxHeight: `${Math.min(
                    availableHeight,
                    sizeOptions.maxHeightCeiling || availableHeight
                  )}px`
                });
              }
            }

            if (sizeOptions.isMaxWidthAdjusted) {
              if (parseInt(elements.floating.style.maxWidth) !== availableWidth) {
                Object.assign(elements.floating.style, {
                  maxWidth: `${Math.min(
                    availableWidth,
                    sizeOptions.maxWidthCeiling || availableWidth
                  )}px`
                });
              }
            }
          },

          padding: boundaryOptions.padding
        })
      );
    }

    const { x, y, reference, floating, refs, update } = useFloating({
      placement: placement,
      middleware: middleware,
      onPosition: _onPosition
    });

    // To solve "loop limit exceeds" problem from the ResizeObserver in autoUpdate
    // we need to make sure that we utilize autoUpdate function
    // after the Floating component is positioned and has x, y coordinates.
    const [isPositioned, setIsPositioned] = useState(false);

    // here autoUpdate should be called only once during the Component lifecycle,
    // if the floating, reference, placement and middleware do not change
    useEffect(() => {
      if (!refs.reference.current || !refs.floating.current || isPositioned !== true) {
        return;
      }

      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }, [refs.floating, refs.reference, update, isPositioned]);

    // here we should know when the Floating is positioned
    useEffect(() => {
      if (x != null && y != null) {
        setIsPositioned(true);
      }
    }, [x, y]);

    useOutsideClick({ isDisabled: false, ref: refs.floating, handler: onClickOutside });

    function createVirtualElement(
      domRef: Element | null,
      data: {
        axis: 'x' | 'y' | 'both';
        x: number | null;
        y: number | null;
      }
    ) {
      let offsetX: number | null = null;
      let offsetY: number | null = null;
      let isAutoUpdateEvent = false;

      return {
        contextElement: domRef || undefined,
        getBoundingClientRect() {
          const domRect = domRef?.getBoundingClientRect() || {
            width: 0,
            height: 0,
            x: 0,
            y: 0
          };

          const isXAxis = data.axis === 'x' || data.axis === 'both';
          const isYAxis = data.axis === 'y' || data.axis === 'both';

          let width = domRect.width;
          let height = domRect.height;
          let x = domRect.x;
          let y = domRect.y;

          if (offsetX == null && data.x && isXAxis) {
            offsetX = domRect.x - data.x;
          }

          if (offsetY == null && data.y && isYAxis) {
            offsetY = domRect.y - data.y;
          }

          x -= offsetX || 0;
          y -= offsetY || 0;
          width = 0;
          height = 0;

          if (!isAutoUpdateEvent) {
            width = data.axis === 'y' ? domRect.width : 0;
            height = data.axis === 'x' ? domRect.height : 0;
            x = isXAxis && data.x != null ? data.x : x;
            y = isYAxis && data.y != null ? data.y : y;
          } else if (isAutoUpdateEvent) {
            height = data.axis === 'x' ? domRect.height : height;
            width = data.axis === 'y' ? domRect.width : width;
          }

          isAutoUpdateEvent = true;
          const virtEl = {
            width,
            height,
            x,
            y,
            top: y,
            right: x + width,
            bottom: y + height,
            left: x
          };

          return virtEl;
        }
      };
    }

    useLayoutEffect(() => {
      if (isElement(previousAnchor)) {
        const element = previousAnchor as Element;
        reference(element);
        return;
      }
      const coords = previousAnchor as Coords;
      if (coords != null && coords.contextElement != null) {
        const virtualEl = createVirtualElement(coords.contextElement, {
          axis: 'both',
          x: coords.x,
          y: coords.y
        });
        reference(virtualEl);
        return;
      }
      const virtualEl: VirtualElement = {
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: coords!.x,
            y: coords!.y,
            top: coords!.y,
            left: coords!.x,
            right: coords!.x,
            bottom: coords!.y
          };
        }
      };
      if (coords && coords.x != null) {
        reference(virtualEl);
      }
    }, [previousAnchor, reference]);

    const stableRef = useMemo(() => mergeRefs(ref, floating), [ref, floating]);
    const positionStyle = { top: y, left: x };

    let assignedVars = {}; // empty var override unless background is specified
    if (backgroundColor) {
      assignedVars = assignInlineVars({
        [floatingVars.backgroundColor]: backgroundColor
      });
    }

    const visibilityStyles =
      x == null && y == null ? floatingVisibilityStyles.hidden : floatingVisibilityStyles.visible;
    const floatingClasses = classNames([className, baseTheme, styles.baseStyle, visibilityStyles]);
    const isAnchorMounted = anchorRef && anchorRef.current;
    const content = (
      <div class={floatingClasses} ref={stableRef} style={{ ...positionStyle, ...assignedVars }}>
        {children}
        {isAnchorMounted && (
          <FloatingTail
            ref={arrowRef}
            backgroundColor={backgroundColor}
            data={positionData}
            anchorRef={anchorRef}
            tail={tail}
            isRtl={isRtl}
          />
        )}
      </div>
    );
    return content;
  }
);
